import React, { createContext, useContext, useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api from "@/lib/api";
import InstituteSwitcher from "@/components/InstituteSwitcher";
import AboutMenu from "@/components/AboutMenu";
import AdmissionInquiryModal from "@/pages/public/polyrotegaon/AdmissionInquiryModal";
import {
  Menu, X, Phone, Mail, MapPin, ExternalLink, ArrowUpRight,
  Facebook, Instagram, Youtube, Linkedin,
} from "lucide-react";

const MitsolContext = createContext(null);
export const useMitsol = () => useContext(MitsolContext);

// Industrial ITI palette: steel navy + safety orange + cream
export const PALETTE = {
  primary: "#0F2A47",
  primaryDark: "#08192E",
  accent: "#F97316",
  accentDark: "#C2410C",
  gold: "#EAB308",
  cream: "#FEF9F0",
  ink: "#0B1220",
};

export const NAV = [
  { to: "", end: true, label: "Home" },
  { to: "about", label: "About", isAboutMenu: true },
  { to: "trades", label: "Trades" },
  { to: "facilities", label: "Facilities" },
  { to: "activities", label: "Activities" },
  { to: "gallery", label: "Gallery" },
  { to: "careers", label: "Careers" },
  { to: "contact", label: "Contact" },
];

const ABOUT_ITEMS = [
  { to: "about", label: "About Us" },
  { to: "leadership", label: "Leadership Message" },
  { to: "executive-body", label: "Executive Body" },
];

export default function MitsolLayout() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/public/website/mitsolapur");
        setData(data);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Website not found");
      } finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    setMobileOpen(false);
  }, [location.pathname]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-white"><div className="text-slate-500 font-serif text-xl">Loading…</div></div>;
  }
  if (err || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-6">
        <div className="text-center">
          <h1 className="font-serif text-3xl text-slate-800">Website not found</h1>
          <p className="mt-2 text-slate-500">{err}</p>
        </div>
      </div>
    );
  }

  const inst = data.institute;
  const logoSrc = inst.logo_url || "/assets/mitcsn-logo.png";

  const linkClass = ({ isActive }) =>
    `text-[13px] px-4 py-2.5 font-semibold uppercase tracking-wider whitespace-nowrap transition-all ${
      isActive
        ? "bg-[#F97316] text-white"
        : "text-white/85 hover:bg-white/10 hover:text-white"
    }`;

  return (
    <MitsolContext.Provider value={data}>
      <Helmet prioritizeSeoTags>
        <title>MIT (ITI) Solapur | Marathwada Private Industrial Training Institute</title>
        <meta name="description" content="Marathwada Private Industrial Training Institute, Solapur — NCVT-affiliated ITI offering Electrician, Fitter, Mechanic Motor Vehicle and Mechanic Diesel trades. Established 1994, managed by G.S. Mandal, Chhatrapati Sambhajinagar." />
        <meta name="keywords" content="MIT ITI Solapur, ITI Solapur, Electrician course Solapur, Fitter ITI Solapur, MMV, Mechanic Diesel, NCVT Solapur, GS Mandal, Tuljapur Road Solapur, Marathwada ITI" />
        <link rel="canonical" href="https://gsmandal.org/mitsolapur" />
      </Helmet>

      <div className="min-h-screen flex flex-col bg-white">
        {/* Top utility bar */}
        <div className="bg-[#0F2A47] text-slate-200 text-[11px]">
          <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-4 flex-wrap">
              {inst.admission_phone && (
                <a href={`tel:${inst.admission_phone.replace(/\s+/g, "")}`} data-testid="mitsol-top-phone" className="inline-flex items-center gap-1 hover:text-[#F97316]"><Phone size={11} className="text-[#F97316]" />{inst.admission_phone}</a>
              )}
              {inst.email && (
                <a href={`mailto:${inst.email}`} data-testid="mitsol-top-email" className="hidden md:inline-flex items-center gap-1 hover:text-[#F97316]"><Mail size={11} className="text-[#F97316]" />{inst.email}</a>
              )}
              <span className="hidden lg:inline-flex items-center gap-1 text-slate-400"><MapPin size={11} className="text-[#F97316]" />Tuljapur Road, Solapur</span>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <InstituteSwitcher currentSlug="mitsolapur" testidPrefix="mitsol-top-switcher" />
              {inst.facebook_url && (<a href={inst.facebook_url} target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="mitsol-top-facebook" className="hover:text-[#F97316]"><Facebook size={13} /></a>)}
              {inst.instagram_url && (<a href={inst.instagram_url} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="mitsol-top-instagram" className="hover:text-[#F97316]"><Instagram size={13} /></a>)}
              {inst.linkedin_url && (<a href={inst.linkedin_url} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" data-testid="mitsol-top-linkedin" className="hover:text-[#F97316]"><Linkedin size={13} /></a>)}
              {inst.youtube_url && (<a href={inst.youtube_url} target="_blank" rel="noopener noreferrer" aria-label="YouTube" data-testid="mitsol-top-youtube" className="hover:text-[#F97316]"><Youtube size={13} /></a>)}
              <NavLink to="fees" data-testid="mitsol-top-know-fees" className="inline-flex items-center gap-1 bg-white/95 hover:bg-white text-[#0F2A47] px-2.5 py-0.5 text-[10px] uppercase tracking-wider font-bold">Know Your Fees</NavLink>
            </div>
          </div>
        </div>

        {/* Logo bar */}
        <div className="bg-white border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-4 py-4 grid grid-cols-[auto_1fr] md:grid-cols-[auto_1fr_auto] items-center gap-4">
            <NavLink to="" end className="flex items-center justify-start shrink-0" data-testid="mitsol-logo-link">
              <img src={logoSrc} alt={inst.name} className="h-16 md:h-20 w-auto object-contain" />
            </NavLink>
            <div className="text-center md:text-center leading-tight">
              <div className="text-[10px] md:text-xs tracking-[0.35em] uppercase text-[#F97316] font-bold">Gramaudyogik Shikshan Mandal&apos;s</div>
              <div className="font-serif font-black text-lg md:text-3xl text-[#0F2A47] mt-1 leading-tight" data-testid="mitsol-header-name">
                MARATHWADA PRIVATE INDUSTRIAL TRAINING INSTITUTE
              </div>
              <div className="font-serif text-sm md:text-base text-slate-700 mt-0.5">Tuljapur Road · Solapur · Est. 1994</div>
              <div className="text-[10px] md:text-xs tracking-[0.15em] uppercase text-slate-500 mt-1">
                {inst.code || "Institute Code · 426"}
              </div>
            </div>
            <div className="hidden md:flex flex-col items-end gap-1">
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#0F2A47] font-bold">NCVT Affiliated</div>
              <div className="text-[10px] tracking-[0.15em] uppercase text-slate-500">Govt. of Maharashtra Recognised</div>
              <button type="button" onClick={() => setInquiryOpen(true)} className="mt-1 inline-flex items-center gap-1.5 bg-[#F97316] hover:bg-[#EA580C] text-white text-[10px] uppercase tracking-wider font-bold px-3 py-1.5">Admission Enquiry <ArrowUpRight size={10} strokeWidth={2.5} /></button>
            </div>
          </div>
        </div>

        {/* Main nav */}
        <nav className="sticky top-0 z-40 bg-[#0F2A47] shadow-md" data-testid="mitsol-nav">
          <div className="max-w-7xl mx-auto px-2">
            <div className="hidden lg:flex items-center gap-0 py-0 flex-wrap">
              {NAV.map((n) => (
                n.isAboutMenu ? (
                  <AboutMenu
                    key="about"
                    linkClass={linkClass}
                    testidPrefix="mitsol-nav-about"
                    menuClassName="bg-[#0F2A47] text-white ring-1 ring-white/10"
                    itemHoverClassName="hover:bg-white/10"
                    items={ABOUT_ITEMS}
                  />
                ) : (
                  <NavLink key={n.to} to={n.to} end={n.end} data-testid={`mitsol-nav-${n.to || "home"}`} className={linkClass}>
                    {n.label}
                  </NavLink>
                )
              ))}
            </div>
            <div className="lg:hidden flex items-center justify-between py-2 px-2">
              <span className="text-white text-[11px] tracking-[0.25em] uppercase font-bold">Menu</span>
              <button onClick={() => setMobileOpen((v) => !v)} className="p-2 text-white" aria-label="Menu" data-testid="mitsol-nav-toggle">{mobileOpen ? <X /> : <Menu />}</button>
            </div>
            {mobileOpen && (
              <div className="lg:hidden pb-3 flex flex-col">
                {NAV.map((n) => (
                  n.isAboutMenu ? (
                    <React.Fragment key="about-mob">
                      {ABOUT_ITEMS.map((it) => (
                        <NavLink
                          key={it.to}
                          to={it.to}
                          end={it.to === "about"}
                          onClick={() => setMobileOpen(false)}
                          data-testid={`mitsol-nav-mob-${it.to}`}
                          className={linkClass}
                        >
                          {it.label}
                        </NavLink>
                      ))}
                    </React.Fragment>
                  ) : (
                    <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setMobileOpen(false)} className={linkClass}>{n.label}</NavLink>
                  )
                ))}
              </div>
            )}
          </div>
        </nav>

        <main className="flex-1"><Outlet /></main>

        {/* Footer */}
        <footer className="bg-[#08192E] text-slate-300">
          <div className="max-w-7xl mx-auto px-5 py-14 grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3">
                <img src={logoSrc} alt={inst.name} className="h-14 w-auto object-contain bg-white p-1.5 rounded" />
                <div>
                  <div className="text-white font-serif text-lg leading-tight">MIT (ITI) Solapur</div>
                  <div className="text-[10px] tracking-[0.25em] uppercase text-[#F97316] font-bold mt-1">A GS Mandal Institution</div>
                </div>
              </div>
              <p className="mt-5 text-sm text-slate-400 leading-relaxed max-w-md">
                Marathwada Private Industrial Training Institute, Solapur — NCVT-affiliated vocational training institute serving Solapur district since August 1994. Committed to skilled manpower, self-employment and industry-ready youth.
              </p>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#F97316] font-bold mb-3">Quick Links</div>
              <ul className="space-y-1.5 text-sm">
                {NAV.map((n) => <li key={n.to}><NavLink to={n.to} end={n.end} className="hover:text-white">{n.label}</NavLink></li>)}
                <li><Link to="fees" className="hover:text-white">Know Your Fees</Link></li>
              </ul>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#F97316] font-bold mb-3">Contact</div>
              <div className="text-sm space-y-2 leading-relaxed">
                <div className="flex items-start gap-2"><MapPin size={13} className="text-[#F97316] mt-0.5 shrink-0" /><span>Tuljapur Road, Tale Hipparga, Ta. North Solapur, Dist. Solapur — Maharashtra</span></div>
                {inst.admission_phone && <div className="flex items-center gap-2"><Phone size={13} className="text-[#F97316]" /><a href={`tel:${inst.admission_phone.replace(/\s+/g, "")}`} className="hover:text-white">{inst.admission_phone}</a></div>}
                {inst.email && <div className="flex items-center gap-2"><Mail size={13} className="text-[#F97316]" /><a href={`mailto:${inst.email}`} className="hover:text-white break-all">{inst.email}</a></div>}
              </div>
              <div className="mt-4 text-[10px] tracking-[0.25em] uppercase text-slate-500">Institute Code · 426 · MIS PR27000276</div>
            </div>
          </div>
          <div className="border-t border-white/10">
            <div className="max-w-7xl mx-auto px-5 py-4 flex items-center justify-between flex-wrap gap-2 text-[11px] text-slate-500">
              <div>© {new Date().getFullYear()} MIT (ITI) Solapur · G.S. Mandal Chhatrapati Sambhajinagar. All rights reserved.</div>
              <div className="flex items-center gap-1">Website powered by <span className="text-[#F97316] font-semibold">GS Mandal CMS</span></div>
            </div>
          </div>
        </footer>
      </div>

      <AdmissionInquiryModal open={inquiryOpen} onClose={() => setInquiryOpen(false)} slug="mitsolapur" departments={data?.departments || []} />
    </MitsolContext.Provider>
  );
}
